// src/components/ui/NudgePopover.js
// Non-blocking tour auto-prompt popover anchored just above the HelpFab (D-20/D-21).
//
// Position: fixed, anchored above the HelpFab at bottom-right.
//   FAB sits at bottom:20, height:44px → popover bottom = 20 + 44 + 8 (gap) = 72px.
//   Right-aligned to match the FAB: right:20px.
//   Both offsets use inline style (JIT trap — never template-literal arbitrary classes).
//
// z-index: z-[9975] — below HelpMenuPanel (z-9985), below HelpFab (z-9980),
//           below Modal (z-9990), above page.
//
// Accessibility (D-10, 47-p2-sweep): the outer popover carries no live-region
// role at all — it is a labelled region containing two ordinary, reachable
// buttons. A sibling sr-only announcement region, mounted as the FIRST
// child, carries the fixed sentence once. The two were previously the SAME
// element (a status-role live region on the outer div), which wrapped both
// buttons — screen readers treat a live region's interactive descendants as
// unreliable to reach, so the buttons were effectively stranded. Splitting
// them fixes that without touching the visual design, position, or props.
// NO scrim, NO scroll-lock (D-21 / UI-SPEC).

import React from 'react';

// Locked sentence (47-UI-SPEC.md § Copywriting Contract, D-10). Names the `?`
// shortcut per D-11. Oxford comma before "or" is intentional — do not alter.
const NUDGE_ANNOUNCEMENT = 'A tour is available for this page. Press ? to jump to it, or keep tabbing to reach it.';

export default function NudgePopover({ body, onStart, onDismiss }) {
    // D-10: the announcement region must NOT enter the DOM with its text
    // already present -- a live region whose element and text arrive in the
    // same paint is frequently never spoken by assistive tech. Holding the
    // sentence in state, set from an effect that fires after mount, forces
    // the text onto a render AFTER the region already exists. Do not
    // "simplify" this to a literal string prop -- that reintroduces the
    // exact same-paint bug this task exists to fix.
    const [announcement, setAnnouncement] = React.useState('');
    React.useEffect(() => {
        setAnnouncement(NUDGE_ANNOUNCEMENT);
    }, []);

    return (
        <div
            role="region"
            aria-label="Tour available"
            data-tour="tour-nudge"
            className="fixed z-[9975] rounded-xl border border-[color:var(--border-color)]
                       bg-[color:var(--surface-primary)]"
            style={{
                bottom: 72,     // 20px (FAB bottom) + 44px (FAB height) + 8px gap
                right: 20,      // right:20 — right-aligns with the HelpFab
                maxWidth: 240,
                padding: '12px 16px',
                boxShadow: 'var(--shadow-lift)',
            }}
        >
            <div aria-live="polite" className="sr-only">{announcement}</div>

            {/* Heading */}
            <p
                className="font-semibold"
                style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}
            >
                New here?
            </p>

            {/* Body copy */}
            <p
                style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}
            >
                {body}
            </p>

            {/* Actions */}
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    data-tour="tour-nudge-start"
                    onClick={onStart}
                    className="text-sm font-medium hover:underline focus:outline-none focus:underline"
                    style={{ color: 'var(--color-accent)' }}
                >
                    Start the tour
                </button>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-sm hover:underline focus:outline-none focus:underline"
                    style={{ color: 'var(--text-muted)' }}
                >
                    Don't show this again
                </button>
            </div>
        </div>
    );
}
